/**
 * Auszahlungen (Spec 8.1): Perioden, Summen je Person, Exporte.
 *
 * Der Ablauf ist bewusst eng geführt: anlegen, prüfen, sperren, exportieren.
 * Gesperrt wird nur, wenn jeder Tag im Zeitraum freigegeben ist.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError, type PeriodDto, type WorkflowDto } from "../lib/api";
import { dateTime, euro, longDate, shortDate } from "../lib/format";
import { isAdmin } from "../lib/auth";
import { useMe } from "./Anmeldung";
import { PeriodPicker } from "../components/PeriodPicker";
import { defaultPeriodId } from "../lib/periodSearch";
import { SortKnopf, Suchfeld, ariaSort, useSortierung } from "../components/Sortierung";
import { passtZurSuche, vergleicheBetrag, vergleicheText, vergleicheZahl } from "../lib/sortierung";
import {
  Amount,
  Empty,
  ErrorBox,
  PageHeader,
  Section,
  Spinner,
  Stat,
  StatusChip,
} from "../components/ui";

interface PeriodSummary {
  id: string;
  label: string;
  dateFrom: string;
  dateTo: string;
  status: "OPEN" | "LOCKED" | "EXPORTED" | "PAID";
  itemCount: number;
  total: string | null;
  exportedAt: string | null;
}

interface PeriodDetail {
  id: string;
  label: string;
  dateFrom: string;
  dateTo: string;
  status: PeriodSummary["status"];
  total: string;
  approvedDays: string[];
  openDays: string[];
  missingIbanCount: number;
  items: {
    employeeId: string;
    personalNr: number;
    timeClockId: number | null;
    name: string;
    totalAmount: string;
    ibanLast4: string | null;
    hasIban: boolean;
  }[];
}

export function Payouts() {
  // `?periode=` kommt etwa von der Übersicht („Überweisungsdatei noch nicht erzeugt“).
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(params.get("periode"));
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const periods = useQuery({
    queryKey: ["payout-periods"],
    queryFn: () => api.get<{ periods: PeriodSummary[] }>("/payout-periods"),
  });

  // Vorne steht die Periode, an der etwas zu tun ist, nicht die älteste.
  useEffect(() => {
    if (selected === null && periods.data) setSelected(defaultPeriodId(periods.data.periods));
  }, [selected, periods.data]);

  const detail = useQuery({
    queryKey: ["payout-period", selected],
    queryFn: () => api.get<PeriodDetail>(`/payout-periods/${selected}/items`),
    enabled: selected !== null,
  });

  const workflow = useQuery({
    queryKey: ["workflow"],
    queryFn: () => api.get<WorkflowDto>("/workflow"),
  });
  const periode = workflow.data?.periode ?? null;
  const nochOffen = periode ? periode.entwurf + periode.berechnet : 0;

  /*
   * Was als Nächstes ausgezahlt wird: der älteste Halbmonat, der vorbei,
   * vollständig freigegeben und noch in keiner Periode ist. Nicht der heutige
   * Kalendermonat — am 23.09. schlug das Formular sonst die erste
   * Septemberhälfte vor, während die zweite Augusthälfte noch offen war.
   */
  const heute = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(new Date());
  const naechste =
    (workflow.data?.perioden ?? [])
      .filter(
        (p) =>
          !p.hatAuszahlung &&
          p.entwurf + p.berechnet === 0 &&
          p.freigegeben > 0 &&
          p.to < heute,
      )
      .sort((a, b) => a.key.localeCompare(b.key))[0] ?? null;
  const bereit = naechste !== null;

  const lock = useMutation({
    mutationFn: () => api.post(`/payout-periods/${selected}/lock`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return (
    <>
      <PageHeader
        label="Auszahlungen"
        title="Perioden"
        actions={
          bereit || creating ? (
            <button className="btn-primary" onClick={() => setCreating((v) => !v)}>
              {creating ? "Abbrechen" : "Periode anlegen"}
            </button>
          ) : null
        }
      >
        Auszahlung erfolgt halbmonatlich. Eine Periode lässt sich erst sperren, wenn jeder
        Betriebstag darin freigegeben ist.
      </PageHeader>

      {/*
        * Eine Periode anzulegen, bevor die Tage freigegeben sind, ergibt eine
        * leere oder halbe Auszahlung — und die fällt erst beim SEPA-Export auf.
        * Deshalb steht hier erst, was noch fehlt, und der Knopf kommt danach.
        */}
      {periode && !bereit && !creating && !periode.hatAuszahlung ? (
        <div className="card mb-6 border-accent/30 p-5">
          <div className="label mb-1">Nächster Schritt</div>
          <h3 className="mb-1 text-lg">{periode.label}</h3>
          <p className="mb-4 max-w-2xl text-sm text-muted">
            {nochOffen > 0 ? (
              <>
                Noch {nochOffen} {nochOffen === 1 ? "Tag" : "Tage"} freigeben
                {periode.entwurf > 0
                  ? ` (${periode.entwurf} davon noch nicht gerechnet)`
                  : ""}
                . Danach lässt sich die Auszahlung anlegen. Vorher wäre sie
                unvollständig, und das fiele erst beim SEPA-Export auf.
              </>
            ) : periode.freigegeben > 0 ? (
              <>
                Bisher ist alles freigegeben. Der Zeitraum läuft aber noch bis{" "}
                {longDate(periode.to)}; die Auszahlung wird angelegt, wenn er vorbei ist.
              </>
            ) : (
              <>
                In diesem Zeitraum ist noch kein Betriebstag freigegeben. Eine Auszahlung
                ohne freigegebene Tage wäre leer.
              </>
            )}
          </p>
          <Link to="/betriebstage" className="btn-recommend">
            Zu den Tagen
          </Link>
        </div>
      ) : null}

      {naechste && !creating ? (
        <div className="card mb-6 border-accent/30 p-5">
          <div className="label mb-1">Bereit zur Auszahlung</div>
          <h3 className="mb-1 text-lg">{naechste.label}</h3>
          <p className="mb-4 max-w-2xl text-sm text-muted">
            Alle {naechste.freigegeben} Betriebstage sind freigegeben. Legen Sie die
            Auszahlungsperiode an, prüfen Sie die Beträge und sperren Sie sie dann.
          </p>
          <button className="btn-recommend" onClick={() => setCreating(true)}>
            Auszahlung anlegen
          </button>
        </div>
      ) : null}

      {creating ? (
        <CreatePeriod
          start={naechste ?? periode}
          onDone={(id) => {
            setCreating(false);
            // Die gerade angelegte Periode zeigen, nicht die älteste offene.
            setSelected(id);
          }}
        />
      ) : null}

      <Section label="Übersicht" title="Alle Perioden">
        {periods.isLoading ? (
          <Spinner />
        ) : (periods.data?.periods.length ?? 0) === 0 ? (
          <Empty>Noch keine Auszahlungsperiode angelegt.</Empty>
        ) : (
          <PeriodPicker
            periods={periods.data?.periods ?? []}
            selectedId={selected}
            onSelect={setSelected}
          />
        )}
      </Section>

      {selected && detail.data ? (
        <Section label="Periode" title={detail.data.label}>
          <div className="mb-6 flex flex-wrap gap-6">
            <Stat label="Summe" value={euro(detail.data.total)} info="Alle Beträge der Periode zusammen. Zählt nur freigegebene Betriebstage." />
            <Stat label="Personen" value={String(detail.data.items.length)} />
            <Stat label="freigegebene Tage" value={String(detail.data.approvedDays.length)} />
            <Stat
              label="offene Tage"
              value={String(detail.data.openDays.length)}
              tone={detail.data.openDays.length > 0 ? "alert" : "ok"}
              info="Tage im Zeitraum mit Trinkgeld, die noch nicht freigegeben sind. Solange einer offen ist, lässt sich die Periode nicht sperren."
            />
            <Stat
              label="ohne IBAN"
              value={String(detail.data.missingIbanCount)}
              tone={detail.data.missingIbanCount > 0 ? "alert" : "ok"}
              info="Personen ohne hinterlegte Bankverbindung. Der SEPA-Export wird verweigert, solange eine fehlt."
            />
          </div>

          {detail.data.openDays.length > 0 ? (
            <div className="mb-4 rounded border border-alert/30 bg-alert/5 px-4 py-3 text-sm">
              <strong className="text-alert">Sperren nicht möglich.</strong> Diese Tage tragen
              Trinkgeld, sind aber nicht freigegeben:{" "}
              {detail.data.openDays.map((d, i) => (
                <span key={d}>
                  {i > 0 ? ", " : ""}
                  <Link to={`/betriebstage/${d}`} className="underline">
                    {longDate(d)}
                  </Link>
                </span>
              ))}
            </div>
          ) : null}

          <div className="mb-6 flex flex-wrap gap-2">
            {/* Sperren gibt es nur, solange die Periode offen ist. */}
            {detail.data.status === "OPEN" ? (
              <button
                className="btn-primary"
                disabled={detail.data.openDays.length > 0 || lock.isPending}
                onClick={() => lock.mutate()}
                title="Friert die Beträge ein und sperrt die Betriebstage"
              >
                {lock.isPending ? "Sperrt" : "Sperren"}
              </button>
            ) : null}
            {/*
              * Ans Brett kommt Papier, keine CSV. Der Druckweg steht deshalb
              * vorn; die CSV bleibt für alle, die damit weiterrechnen wollen.
              */}
            <Link
              className="btn-primary"
              to={`/aushang?monat=${detail.data.dateFrom.slice(0, 7)}&haelfte=${Number(detail.data.dateFrom.slice(8, 10)) <= 15 ? 1 : 2}&von=auszahlungen`}
              title="Einseitige Übersicht je Stempeluhr-ID, druckfertig"
            >
              Aushang drucken
            </Link>
            <a
              className="btn-ghost"
              href={`/api/v1/payout-periods/${selected}/export/anonymous`}
              title="Dieselben Zahlen als CSV, zum Weiterrechnen"
            >
              Aushang als CSV
            </a>
          </div>

          {lock.error ? <ErrorBox error={lock.error} /> : null}

          {detail.data.status === "PAID" ? (
            <p className="mb-6 text-sm text-muted">
              Ausgezahlt. Für diese Periode gibt es keine Überweisungsdatei mehr, damit nichts
              doppelt überwiesen wird.
            </p>
          ) : (
            <BankExport
              periodId={detail.data.id}
              status={detail.data.status}
              total={detail.data.total}
              count={detail.data.items.filter((i) => i.totalAmount !== "0.00").length}
              missingIbanCount={detail.data.missingIbanCount}
              exportedAt={periods.data?.periods.find((p) => p.id === detail.data.id)?.exportedAt ?? null}
            />
          )}

          <PersonenDerAuszahlung items={detail.data.items} />
        </Section>
      ) : null}
    </>
  );
}

interface PayoutAccountDto {
  account: { name: string; iban: string; bic: string | null } | null;
  nextExecutionDate: string;
}

/**
 * Die Überweisungsdatei für die Bank (SEPA, pain.001): eine
 * Sammelüberweisung für die ganze Periode, im Online-Banking hochzuladen.
 * Ersetzt den Weg über die Excel-Vorlage von SEPAApp.
 */
function BankExport({
  periodId,
  status,
  total,
  count,
  missingIbanCount,
  exportedAt,
}: {
  periodId: string;
  status: PeriodSummary["status"];
  total: string;
  count: number;
  missingIbanCount: number;
  exportedAt: string | null;
}) {
  const queryClient = useQueryClient();
  const admin = isAdmin(useMe().data?.user);
  const account = useQuery({
    queryKey: ["payout-account"],
    queryFn: () => api.get<PayoutAccountDto>("/payout-account"),
  });
  const [date, setDate] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const executionDate = date ?? account.data?.nextExecutionDate ?? "";

  const download = useMutation({
    mutationFn: () =>
      api.download(
        `/payout-periods/${periodId}/export/sepa?ausfuehrung=${encodeURIComponent(executionDate)}`,
        "ueberweisung.xml",
      ),
    onSuccess: async (name) => {
      setSaved(name);
      await queryClient.invalidateQueries({ queryKey: ["payout-periods"] });
    },
  });

  const acc = account.data?.account ?? null;
  const blocked =
    status === "PAID"
      ? "Bereits ausgezahlt. Eine weitere Datei würde doppelt überweisen."
      : status === "OPEN"
      ? "Erst sperren: danach ändern sich die Beträge nicht mehr."
      : missingIbanCount > 0
        ? `${missingIbanCount} ${missingIbanCount === 1 ? "Person hat" : "Personen haben"} keine IBAN. Eine unvollständige Überweisung gibt es nicht.`
        : !acc
          ? "Es fehlt das Konto, von dem überwiesen wird."
          : null;

  return (
    <div className="card mb-6 p-5">
      <div className="label mb-1">Überweisung an die Bank</div>
      <p className="mb-4 max-w-2xl text-sm text-muted">
        Eine Datei mit allen {count} Überweisungen über zusammen {euro(total)}. Im Online-Banking
        als SEPA-Sammelüberweisung hochladen; die Bank zeigt vor der Freigabe Anzahl und Summe
        zum Vergleich.
      </p>

      <div className="mb-4 text-sm">
        <span className="label mr-2">Von</span>
        {account.isLoading ? (
          <Spinner />
        ) : acc ? (
          <span>
            {acc.name} · <span className="font-mono">{acc.iban}</span>
            {acc.bic ? <span className="font-mono text-muted"> · {acc.bic}</span> : null}
          </span>
        ) : (
          <span className="chip-alert">kein Konto eingetragen</span>
        )}
        {admin ? (
          <button className="btn-ghost ml-2" onClick={() => setEditing((v) => !v)}>
            {editing ? "Abbrechen" : acc ? "Ändern" : "Eintragen"}
          </button>
        ) : !acc ? (
          <span className="ml-2 text-xs text-muted">Das trägt die Administration ein.</span>
        ) : null}
      </div>

      {editing ? <PayoutAccountForm current={acc} onDone={() => setEditing(false)} /> : null}

      {exportedAt ? (
        <div className="mb-4 rounded border border-alert/30 bg-alert/5 px-4 py-3 text-sm">
          <strong className="text-alert">Bereits am {dateTime(exportedAt)} erzeugt.</strong> Jede
          hochgeladene Datei überweist erneut. Nur neu erzeugen, wenn die erste Datei nicht bei
          der Bank angekommen ist.
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="label mb-1 block">Ausführungsdatum</span>
          <input
            type="date"
            className="field"
            value={executionDate}
            min={account.data?.nextExecutionDate}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <button
          className="btn-primary"
          disabled={blocked !== null || !executionDate || download.isPending}
          title={blocked ?? undefined}
          onClick={() => {
            setSaved(null);
            download.mutate();
          }}
        >
          {download.isPending ? "Erzeugt …" : "Überweisungsdatei herunterladen"}
        </button>
      </div>
      {blocked ? <p className="mt-2 text-xs text-muted">{blocked}</p> : null}
      {saved ? (
        <p className="mt-2 text-sm">
          <strong>{saved}</strong> gespeichert. Jetzt im Online-Banking hochladen und dort Anzahl
          ({count}) und Summe ({euro(total)}) vergleichen.
        </p>
      ) : null}
      {download.error ? (
        <div className="mt-3">
          <ErrorBox error={download.error} />
        </div>
      ) : null}
    </div>
  );
}

function PayoutAccountForm({
  current,
  onDone,
}: {
  current: PayoutAccountDto["account"];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(current?.name ?? "");
  const [iban, setIban] = useState(current?.iban ?? "");
  const [bic, setBic] = useState(current?.bic ?? "");
  const save = useMutation({
    mutationFn: () => api.put("/payout-account", { name, iban, bic: bic.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payout-account"] });
      onDone();
    },
  });
  return (
    <form
      className="mb-4 grid gap-3 rounded bg-surface p-4 md:grid-cols-[1fr_1fr_12rem_auto]"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <label className="block">
        <span className="label mb-1 block">Name am Konto</span>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bhf. Ehrenfeld GmbH" />
      </label>
      <label className="block">
        <span className="label mb-1 block">IBAN</span>
        <input
          className="field font-mono"
          value={iban}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setIban(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="label mb-1 block">BIC (freiwillig)</span>
        <input
          className="field font-mono"
          value={bic}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setBic(e.target.value)}
        />
      </label>
      <div className="flex items-end">
        <button className="btn-primary" type="submit" disabled={save.isPending || !name.trim() || iban.trim().length < 15}>
          {save.isPending ? "Speichert …" : "Speichern"}
        </button>
      </div>
      {save.error ? (
        <div className="md:col-span-4">
          <ErrorBox error={save.error} />
        </div>
      ) : null}
    </form>
  );
}

function CreatePeriod({ start, onDone }: { start: PeriodDto | null; onDone: (id: string) => void }) {
  // Startwert ist der Halbmonat, um den es geht — nicht das heutige Datum.
  const today = new Date();
  const [year, setYear] = useState(start ? Number(start.month.slice(0, 4)) : today.getFullYear());
  const [month, setMonth] = useState(start ? Number(start.month.slice(5, 7)) : today.getMonth() + 1);
  const [half, setHalf] = useState<1 | 2>(start?.key.endsWith("H2") ? 2 : 1);
  const queryClient = useQueryClient();

  const suggestion = useQuery({
    queryKey: ["payout-suggest", year, month, half],
    queryFn: () =>
      api.get<{ label: string; dateFrom: string; dateTo: string }>(
        `/payout-periods/suggest?year=${year}&month=${month}&half=${half}`,
      ),
  });

  const create = useMutation({
    mutationFn: () => api.post<{ id: string; label: string }>("/payout-periods", suggestion.data),
    onSuccess: async (neu) => {
      await queryClient.invalidateQueries();
      onDone(neu.id);
    },
  });

  return (
    <div className="card mb-8 p-5">
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="label mb-1 block">Jahr</span>
          <input
            type="number"
            className="field"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
        <label className="block">
          <span className="label mb-1 block">Monat</span>
          <select
            className="field"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {new Date(2000, m - 1, 1).toLocaleDateString("de-DE", { month: "long" })}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label mb-1 block">Hälfte</span>
          <select
            className="field"
            value={half}
            onChange={(e) => setHalf(Number(e.target.value) as 1 | 2)}
          >
            <option value={1}>erste</option>
            <option value={2}>zweite</option>
          </select>
        </label>
      </div>

      {suggestion.data ? (
        <p className="mb-4 text-sm text-muted">
          <span className="label mr-2">Vorschlag</span>
          {suggestion.data.label} · {suggestion.data.dateFrom} bis {suggestion.data.dateTo}
        </p>
      ) : null}

      {create.error ? (
        <div className="mb-4">
          <ErrorBox
            error={
              create.error instanceof ApiError && create.error.code === "overlapping_period"
                ? new Error(create.error.message)
                : create.error
            }
          />
        </div>
      ) : null}

      <button
        className="btn-primary"
        onClick={() => create.mutate()}
        disabled={!suggestion.data || create.isPending}
      >
        {create.isPending ? "Legt an" : "Anlegen"}
      </button>
    </div>
  );
}

/**
 * Wer wie viel bekommt, nach Name oder Betrag sortierbar und durchsuchbar
 * (Rückmeldung Oktober 2026). Vorgabe bleibt die gewohnte Reihenfolge nach Namen.
 */
function PersonenDerAuszahlung({ items }: { items: PeriodDetail["items"] }) {
  const [suche, setSuche] = useState("");
  const [sortierung, umschalten] = useSortierung<"persnr" | "name" | "betrag">({
    spalte: "name",
    richtung: "auf",
  });
  const { spalte, richtung } = sortierung;
  const zeilen = items
    .filter((i) => passtZurSuche(suche, i.name, i.personalNr, i.timeClockId))
    .sort((a, b) =>
      spalte === "name"
        ? vergleicheText(a.name, b.name, richtung)
        : spalte === "betrag"
          ? vergleicheBetrag(a.totalAmount, b.totalAmount, richtung)
          : vergleicheZahl(a.personalNr, b.personalNr, richtung),
    );
  const kopf = (s: typeof spalte, text: string, ersteRichtung: "auf" | "ab" = "auf") => (
    <SortKnopf aktiv={spalte === s} richtung={richtung} onClick={() => umschalten(s, ersteRichtung)}>
      {text}
    </SortKnopf>
  );

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Suchfeld wert={suche} onChange={setSuche} platzhalter="Name, PersNr oder Stempeluhr-ID suchen" />
      </div>
      {zeilen.length === 0 ? (
        <Empty>Niemand passt zu „{suche}“.</Empty>
      ) : (
        <div className="card overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th className="w-20" aria-sort={ariaSort(spalte === "persnr", richtung)}>
                  {kopf("persnr", "PersNr")}
                </th>
                <th aria-sort={ariaSort(spalte === "name", richtung)}>{kopf("name", "Name")}</th>
                <th className="w-28">Stempeluhr</th>
                <th className="w-28">IBAN</th>
                <th className="w-32 text-right" aria-sort={ariaSort(spalte === "betrag", richtung)}>
                  {kopf("betrag", "Betrag", "ab")}
                </th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((i) => (
                <tr key={i.employeeId}>
                  <td className="font-mono text-xs text-muted">{i.personalNr}</td>
                  <td>{i.name}</td>
                  <td className="font-mono text-xs text-muted">{i.timeClockId ?? "–"}</td>
                  <td>
                    {i.hasIban ? (
                      <span className="font-mono text-xs text-muted">
                        ···· {i.ibanLast4 ?? "····"}
                      </span>
                    ) : (
                      <Link
                        to={`/stammdaten?tab=mitarbeiter&suche=${i.personalNr}`}
                        className="chip-alert hover:underline"
                        title="In den Stammdaten eintragen"
                      >
                        fehlt, eintragen
                      </Link>
                    )}
                  </td>
                  <td>
                    <Amount value={euro(i.totalAmount)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
